import type { SuggestionListParams } from '../adminApi'
import type { SuggestionItem } from '../types'

export interface SuggestionFilters {
  module: string
  subtype: string
  status: string
  /** Render the page grouped under area headers instead of one serial list. */
  grouped: boolean
  page: number
}

export const DEFAULT_SUGGESTION_FILTERS: SuggestionFilters = { module: '', subtype: '', status: '', grouped: false, page: 1 }
export const SUGGESTION_PAGE_SIZE = 50

const SUBTYPES = ['improve_existing', 'new_feature']

export function suggestionFiltersFromSearch(search: string | URLSearchParams): SuggestionFilters {
  const p = typeof search === 'string' ? new URLSearchParams(search) : search
  const subtype = p.get('subtype') ?? ''
  const page = Number.parseInt(p.get('page') ?? '', 10)
  return {
    module: p.get('module') ?? '',
    subtype: SUBTYPES.includes(subtype) ? subtype : '',
    status: p.get('status') ?? '',
    grouped: p.get('group') === 'area',
    page: Number.isFinite(page) && page >= 1 ? page : 1,
  }
}

export function searchFromSuggestionFilters(f: SuggestionFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.module) p.set('module', f.module)
  if (f.subtype) p.set('subtype', f.subtype)
  if (f.status) p.set('status', f.status)
  if (f.grouped) p.set('group', 'area')
  if (f.page > 1) p.set('page', String(f.page))
  return p
}

export function toSuggestionListParams(f: SuggestionFilters): SuggestionListParams {
  return {
    module: f.module || undefined,
    subtype: f.subtype || undefined,
    status: f.status || undefined,
    page: f.page,
    page_size: SUGGESTION_PAGE_SIZE,
  }
}

export function hasActiveSuggestionFilters(f: SuggestionFilters): boolean {
  return Boolean(f.module || f.subtype || f.status)
}

export const NO_AREA = 'No area'

/** Items bucketed by area, areas alphabetical with "No area" last; order inside each bucket is preserved. */
export function groupByArea(items: SuggestionItem[]): { area: string; items: SuggestionItem[] }[] {
  const buckets = new Map<string, SuggestionItem[]>()
  for (const item of items) {
    const key = item.module ?? NO_AREA
    const list = buckets.get(key) ?? []
    list.push(item)
    buckets.set(key, list)
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => (a === NO_AREA ? 1 : b === NO_AREA ? -1 : a.localeCompare(b)))
    .map(([area, list]) => ({ area, items: list }))
}
